import { isAxiosError } from "axios";

/**
 * Extract a human-readable message from a failed API call.
 *
 * Backend error body: `{ statusCode, message, error, code? }` where `message`
 * is a string, or a string[] for class-validator (400) failures. Some auth
 * errors nest it as `{ error: { code, message } }`.
 *
 * Plain `Error`s pass their message through: services such as
 * CouponService already rethrow API failures as readable `Error`s.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    if (!error.response) {
      return error.code === "ECONNABORTED"
        ? "The request timed out. Please try again."
        : "Unable to reach the server. Check your connection and try again.";
    }

    const data = error.response.data;
    const message = data?.error?.message ?? data?.message;

    if (Array.isArray(message) && message.length > 0) {
      return message.map(capitalize).join(". ");
    }
    if (typeof message === "string" && message.trim()) {
      return capitalize(message);
    }
    return fallback;
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
